import { isEqual, orderBy, sortBy } from 'lodash';
import { createCachedSelector } from 're-reselect';
import { createSelector } from 'reselect';
import { State } from 'store/types';
import {
  AssessmentQuestionType,
  ReportingVisualsSizeEnum,
  createSelectorCached,
  genericReportingVisualsSize,
} from 'types/types';

import {
  arrayUnique,
  objectHasOwnProperty,
  validateEnumFallback,
} from 'lib/riskbase';

import { selectGeneric } from 'store/app/selectors';
import { ReportEnum } from 'types/Reports';
import { EngageDocument, EngageElement } from 'types/EngageSettings';
import { AssessmentLibrary } from './types';
import { returnAseFindingMatchingSearchTerm } from './utils';
import { ICONS } from 'components/Icon';

type RemoteState = Omit<Partial<State>, 'remote'> & Pick<State, 'remote'>;

const selectRemote = (state: RemoteState): RemoteState['remote'] =>
  state.remote;

export const selectOrganisationFieldSettingsIsLoaded = createSelector(
  selectRemote,
  (state): boolean => !!state.organisationFieldSettings,
);

export const selectOrganisationFieldSettings = createSelector(
  selectRemote,
  (state) => state.organisationFieldSettings!,
);

export const selectOrganisationFieldSettingsResultCount = createSelector(
  selectRemote,
  (state) => state.organisationFieldResultCount,
);

export const selectFieldSettingsSectionId = createSelector(
  selectRemote,
  (state) => state.fieldSettingsOpened.sectionId,
);

export const selectFieldSettingsGroupId = createSelector(
  selectRemote,
  (state) => state.fieldSettingsOpened.groupId,
);

export const selectFieldSettingsGroupCheckTypes = createSelector(
  selectRemote,
  (state) => state.organisationFieldSettings?.checkTypes || {},
);

export const selectFieldSettingsFieldId = createSelector(
  selectRemote,
  (state) => state.fieldSettingsOpened.fieldId,
);

export const selectFieldSettingsLiteGroupId = createSelector(
  selectRemote,
  (state) => state.fieldSettingsOpened.liteGroupId,
);

/**
 * SETUP
 */
export const selectRemoteOrganisationId = createSelector(
  selectRemote,
  (remote) => remote.organisationId,
);

export const selectRemoteOrganisation = createSelector(
  selectRemote,
  (remote) => remote.organisation,
);

export const selectRemoteCustomers = createSelector(
  selectRemote,
  (remote) => remote.customers,
);

export const selectRemoteCustomer = createCachedSelector(
  [selectRemoteCustomers, (_: State, customerId: string) => customerId],
  (customers, customerId) => customers?.[customerId],
)((_: State, customerId) => customerId);

/**
 * ASSESSMENT SETUP Template
 */

export const selectRemoteFieldSectionIdsByOrganisation = createSelector(
  selectRemote,
  (remote) => remote.fieldSectionIdsByOrganisation,
);

export const selectRemoteFieldSections = createSelector(
  selectRemote,
  (remote) => remote.fieldSections,
);

export const selectRemoteFieldGroups = createSelector(
  selectRemote,
  (remote) => remote.fieldGroups,
);

export const selectRemoteFieldFields = createSelector(
  selectRemote,
  (remote) => remote.fieldFields,
);

export const selectRemoteFieldGroupCheckTypes = createSelector(
  selectRemote,
  (remote) => remote.fieldGroupCheckTypes,
);

export const selectRemoteCategories = createSelector(
  selectRemote,
  (remote) => remote.categories,
);

export const selectRemoteCategoryGroups = createSelector(
  selectRemote,
  (remote) => remote.categoryGroups,
);

export const selectRemoteSeverities = createSelector(
  selectRemote,
  (remote) => remote.severities,
);

export const selectRemoteTimescales = createSelector(
  selectRemote,
  (remote) => remote.timescales,
);

export const selectRemoteDueTypes = createSelector(
  selectRemote,
  (remote) => remote.dueTypes,
);

const selectRemoteComplianceTypes = createSelector(
  selectRemote,
  (remote) => remote.complianceTypes,
);

export const selectRemoteComplianceSettingComplianceAndAssessmentTypes =
  createSelector(selectRemote, (remote) => remote.complianceTypeSettings);

export const selectRemoteComplianceSettingComplianceTypes = createSelector(
  selectRemoteComplianceSettingComplianceAndAssessmentTypes,
  (remote) => remote?.complianceTypes,
);

export const selectRemoteComplianceSettingAssessmentTypes = createSelector(
  selectRemoteComplianceSettingComplianceAndAssessmentTypes,
  (remote) => remote?.assessmentTypes,
);

export const selectRemoteComplianceSettingComplianceType = createSelector(
  selectRemoteComplianceSettingComplianceAndAssessmentTypes,
  (_: State, resourceId: string | null) => resourceId,
  (state, resourceId: string | null) => {
    return resourceId ? state?.complianceTypes?.[resourceId] : undefined;
  },
);

export const selectRemoteComplianceSettingAssessmentType = createSelector(
  selectRemoteComplianceSettingComplianceAndAssessmentTypes,
  (_: State, resourceId: string | null) => resourceId,
  (state, resourceId: string | null) => {
    return resourceId ? state?.assessmentTypes?.[resourceId] : undefined;
  },
);

const selectRemoteComplianceSubtypes = createSelector(
  selectRemote,
  (remote) => remote.complianceSubtypes,
);

export const selectRemoteAssessmentSchemes = createSelector(
  selectRemote,
  (remote) => remote.assessmentSchemes,
);

export const selectRemoteAssessmentFlags = createSelector(
  selectRemote,
  (remote) => remote.assessmentFlags,
);

export const selectRemoteAssessmentSignatories = createSelector(
  selectRemote,
  (remote) => remote.assessmentSignatories,
);

export const selectRemoteAssessmentPdfs = createSelector(
  selectRemote,
  (remote) => remote.assessmentPdfs,
);

export const selectRemoteAssessmentTemplateDefaultCosts = createSelector(
  selectRemote,
  (remote) => remote.assessmentTemplateDefaultCosts,
);

export const selectRemoteAssessmentValidityTimescales = createSelector(
  selectRemote,
  (remote) => remote.assessmentValidityTimescales,
);

export const selectRemoteComplianceSubtypesWithType = createSelector(
  selectRemoteComplianceTypes,
  selectRemoteComplianceSubtypes,
  (
    types,
    subtypes,
  ): Record<
    string,
    {
      id: number;
      name: string;
      complianceTypeId: number;
      complianceTypeName: string;
    }
  > =>
    Object.fromEntries(
      Object.entries(subtypes!).map(([subtypeId, subtype]) => {
        return [
          subtypeId,
          {
            id: subtype.id,
            name: subtype.name,
            complianceTypeName: types![subtype.complianceTypeId]!.name,
            complianceTypeId: types![subtype.complianceTypeId]!.id,
          },
        ];
      }),
    ),
);

export const selectRemoteSettingsAssessmentChecks = createSelector(
  selectRemote,
  (remote) => remote.settingsAssessmentChecks,
);

export const selectRemoteSettingsAssessmentCheck = createCachedSelector(
  selectRemoteSettingsAssessmentChecks,
  (_: State, checkId: string) => checkId,
  (checks, checkId) => checks?.[checkId],
)((_: State, checkId: string) => checkId);

const selectAseSetupTemplateOpened = createSelector(
  selectRemote,
  (remote) => remote.assessmentTemplateOpened,
);

export const selectRemoteAssessmentTemplates = createSelector(
  selectRemote,
  (remote) => remote.assessmentTemplates,
);

export const selectAseSetupTplTemplateId = createSelector(
  selectAseSetupTemplateOpened,
  (opened) => opened.templateId,
);

export const selectRemoteAssessmentTemplate = createCachedSelector(
  selectRemoteAssessmentTemplates,
  (_: RemoteState, templateId: number) => templateId,
  (templates, templateId) => templates![templateId],
)((_, templateId) => templateId);

/**
 * ASSESSMENT SETUP
 */

// const selectAseSetupSettings = createSelector(
//   selectRemote,
//   (remote) => remote.assessmentLibrarySettings,
// );

const selectAseSetupOpened = createSelector(
  selectRemote,
  (remote) => remote.assessmentLibraryOpened,
);

const selectAseSetupLibrary = createSelector(
  selectRemote,
  (remote) => remote.assessmentLibrary!,
);

export const selectAseSetupSectionId = createSelector(
  selectAseSetupOpened,
  (opened) => opened.sectionId,
);

export const selectAseSetupQuestionId = createSelector(
  selectAseSetupOpened,
  (opened) => opened.questionId,
);

export const selectAseSetupChecklistItemId = createSelector(
  selectAseSetupOpened,
  (opened) => opened.checklistItemId,
);

export const selectAseSetupAnswerId = createSelector(
  selectAseSetupOpened,
  (opened) => opened.answerId,
);

export const selectAseSetupActionId = createSelector(
  selectAseSetupOpened,
  (opened) => opened.actionId,
);

export const selectAseSetupControlId = createSelector(
  selectAseSetupOpened,
  (opened) => opened.controlId,
);

export const selectAseSetupLibraryFolders = createSelector(
  selectAseSetupLibrary,
  (library) => library.folders,
);

export const selectAseSetupLibrarySections = createSelector(
  selectAseSetupLibrary,
  (library) => library.sections,
);

export const selectAseSetupLibraryQuestions = createSelector(
  selectAseSetupLibrary,
  (library) => library.questions,
);

export const selectAseSetupLibraryAnswers = createSelector(
  selectAseSetupLibrary,
  (library) => library.answers,
);

export const selectAseSetupLibraryChecklistItems = createSelector(
  selectAseSetupLibrary,
  (library) => library.checklistItems,
);

export const selectAseSetupLibraryActionDefinitions = createSelector(
  selectAseSetupLibrary,
  (library) => library.actionDefinitions,
);

const selectAseSetupSuggestions = createSelector(
  selectRemote,
  (remote) => remote.assessmentSuggestions!,
);

const selectAseSetupActions = createSelector(
  selectAseSetupSuggestions,
  (suggestions) => suggestions.actions,
);

const selectAseSetupSuggestedActionsAlphaOrdered = createCachedSelector(
  selectAseSetupActions,
  selectRemoteCategories,
  selectRemoteSeverities,
  selectRemoteTimescales,
  (_: State, filterKey: string) => selectGeneric(_, filterKey || '_'),
  (actions, categories, severities, timescales, filterValue) =>
    sortBy(
      typeof filterValue === 'string' && filterValue
        ? returnAseFindingMatchingSearchTerm(
            Object.values(actions),
            filterValue,
            {
              categories,
              severities,
              timescales,
            },
          )
        : Object.values(actions),
      'what',
    ),
)((_: State, filterKey: string) => filterKey || '_');

export const selectAseSetupLibraryActionIdsAlphaOrdered = createCachedSelector(
  selectAseSetupSuggestedActionsAlphaOrdered,
  (actions) => actions.map((action) => action.id),
)((_: State, filterKey: string) => filterKey || '_');

const selectAseSetupControls = createSelector(
  selectAseSetupSuggestions,
  (suggestions) => suggestions.controls,
);

const selectAseSetupSuggestedControlsAlphaOrdered = createCachedSelector(
  selectAseSetupControls,
  selectRemoteCategories,
  selectRemoteSeverities,
  selectRemoteTimescales,
  (_: State, filterKey: string) => selectGeneric(_, filterKey || '_'),
  (controls, categories, severities, timescales, filterValue) =>
    sortBy(
      typeof filterValue === 'string' && filterValue
        ? returnAseFindingMatchingSearchTerm(
            Object.values(controls),
            filterValue,
            {
              categories,
              severities,
              timescales,
            },
          )
        : Object.values(controls),
      'what',
    ),
)((_: State, filterKey: string) => filterKey || '_');

export const selectAseSetupLibraryControlIdsAlphaOrdered = createCachedSelector(
  selectAseSetupSuggestedControlsAlphaOrdered,
  (controls) => controls.map((control) => control.id),
)((_: State, filterKey: string) => filterKey || '_');

export const selectAseSetupLibraryOrganisationHasFolders = createSelector(
  selectAseSetupLibraryFolders,
  (folders) =>
    orderBy(Object.values(folders), 'ordering').reduce<number[]>(
      (acc, folder) => {
        acc.push(folder.id);
        return acc;
      },
      [],
    ),
);

const selectAseSetupLibraryTemplatesHaveQuestions = createSelector(
  selectAseSetupLibraryQuestions,
  (questions) =>
    Object.values(questions).reduce<Record<string, number[]>>(
      (acc, question) => {
        if (question.sectionId) {
          question.templateIds.forEach((templateId) => {
            if (!acc[templateId]) {
              acc[templateId] = [];
            }
            acc[templateId]!.push(question.id);
          });
        }
        return acc;
      },
      {},
    ),
);

export const selectAseSetupLibraryTemplateHasQuestions = createCachedSelector(
  selectAseSetupLibraryTemplatesHaveQuestions,
  (_: RemoteState, templateId: number) => templateId,
  (templatesHaveQuestions, templateId): number[] =>
    templatesHaveQuestions[templateId] || [],
)((_, templateId) => templateId);

const selectAseSetupLibraryFoldersHaveSections = createSelector(
  selectAseSetupLibrarySections,
  (sections) =>
    orderBy(Object.values(sections), 'ordering').reduce<
      Record<string, number[]>
    >((acc, section) => {
      if (!acc[section.folderId]) {
        acc[section.folderId] = [];
      }
      acc[section.folderId]!.push(section.id);
      return acc;
    }, {}),
);

export const selectAseSetupLibraryFolderHasSections = createCachedSelector(
  selectAseSetupLibraryFoldersHaveSections,
  (_: RemoteState, folderId: number) => folderId,
  (foldersHaveSections, folderId) => foldersHaveSections[folderId] ?? [],
)((_, folderId) => folderId);

const selectAseSetupLibrarySectionsHaveQuestions = createSelector(
  selectAseSetupLibraryQuestions,
  (questions) =>
    orderBy(Object.values(questions), 'ordering').reduce<
      Record<string, number[]>
    >((acc, question) => {
      if (question.sectionId) {
        if (!acc[question.sectionId]) {
          acc[question.sectionId] = [];
        }
        acc[question.sectionId]!.push(question.id);
      }
      return acc;
    }, {}),
);

export const selectAseSetupLibrarySectionHasQuestions = createCachedSelector(
  selectAseSetupLibrarySectionsHaveQuestions,
  (_: RemoteState, sectionId: number) => sectionId,
  (sectionsHaveQuestions, sectionId) => sectionsHaveQuestions[sectionId] ?? [],
)((_, sectionId) => sectionId);

const selectAseSetupLibraryQuestionsHaveAnswers = createSelector(
  selectAseSetupLibraryAnswers,
  (answers) =>
    orderBy(Object.values(answers), 'ordering').reduce<
      Record<string, number[]>
    >((acc, answer) => {
      if (!acc[answer.questionId]) {
        acc[answer.questionId] = [];
      }
      acc[answer.questionId]!.push(answer.id);
      return acc;
    }, {}),
);

export const selectAseSetupLibraryQuestionHasAnswers = createCachedSelector(
  selectAseSetupLibraryQuestionsHaveAnswers,
  (_: RemoteState, questionId: number) => questionId,
  (questionsHaveAnswers, questionId) => questionsHaveAnswers[questionId] ?? [],
)((_, questionId) => questionId);

export const selectAseSetupLibraryQuestionAnswers = createCachedSelector(
  selectAseSetupLibraryQuestionHasAnswers,
  selectAseSetupLibraryAnswers,
  (answerIds, answers) => answerIds.map((answerId) => answers[answerId]!),
)((_, questionId: number) => questionId);

const selectAseSetupLibraryAnswersHaveQuestions = createSelector(
  selectAseSetupLibraryQuestions,
  (questions) =>
    orderBy(Object.values(questions), 'ordering').reduce<
      Record<string, number[]>
    >((acc, question) => {
      if (question.answerId) {
        if (!acc[question.answerId]) {
          acc[question.answerId] = [];
        }
        acc[question.answerId]!.push(question.id);
      }
      return acc;
    }, {}),
);

export const selectAseSetupLibraryAnswerHasQuestions = createCachedSelector(
  selectAseSetupLibraryAnswersHaveQuestions,
  (_: RemoteState, answerId: number) => answerId,
  (answersHaveQuestions, answerId) => answersHaveQuestions[answerId] ?? [],
)((_, answerId) => answerId);

const selectAseSetupLibraryCollapsedSections = createSelector(
  selectRemote,
  (remote) => remote.aseLibraryCollapsedSections,
);

export const selectAseSetupLibraryFolderList = createSelector(
  selectAseSetupLibraryOrganisationHasFolders,
  selectAseSetupLibraryFoldersHaveSections,
  selectAseSetupLibrarySectionsHaveQuestions,
  selectAseSetupLibraryQuestionsHaveAnswers,
  selectAseSetupLibraryAnswersHaveQuestions,
  selectAseSetupLibraryFolders,
  selectAseSetupLibraryQuestions,
  (
    folderIds,
    folderHasSections,
    sectionHasQuestions,
    questionHasAnswers,
    answerHasQuestions,
    folders,
    questions,
  ) => {
    type Item = {
      id: number;
      name: string;
      sectionCount: number;
      questionCount: number;
      checklistCount: number;
    };

    const countQuestions = (questionIds: number[]): number =>
      questionIds.reduce(
        (count, questionId) =>
          (questionHasAnswers[questionId] ?? []).reduce(
            (subCount, answerId) =>
              subCount + countQuestions(answerHasQuestions[answerId] ?? []),
            count,
          ),
        questionIds.filter(
          (questionId) =>
            questions[questionId]?.type === AssessmentQuestionType.question,
        ).length,
      );

    const countChecklists = (questionIds: number[]): number =>
      questionIds.reduce(
        (count, questionId) =>
          (questionHasAnswers[questionId] ?? []).reduce(
            (subCount, answerId) =>
              subCount + countChecklists(answerHasQuestions[answerId] ?? []),
            count,
          ),
        questionIds.filter(
          (questionId) =>
            questions[questionId]?.type === AssessmentQuestionType.checklist,
        ).length,
      );

    return folderIds.map<Item>((folderId) => ({
      id: folderId,
      name: folders[folderId]?.name || 'Unknown',
      sectionCount: (folderHasSections[folderId] ?? []).length,
      questionCount: (folderHasSections[folderId] ?? []).reduce(
        (acc, sectionId) =>
          acc + countQuestions(sectionHasQuestions[sectionId] ?? []),
        0,
      ),
      checklistCount: (folderHasSections[folderId] ?? []).reduce(
        (acc, sectionId) =>
          acc + countChecklists(sectionHasQuestions[sectionId] ?? []),
        0,
      ),
    }));
  },
);

export const selectAseSetupLibraryItemList = createSelector(
  selectAseSetupLibraryOrganisationHasFolders,
  selectAseSetupLibraryFoldersHaveSections,
  selectAseSetupLibrarySectionsHaveQuestions,
  (folderIds, folderHasSections, sectionHasQuestions) => {
    type Item = {
      type: 'folder' | 'section' | 'question';
      id: number;
      firstSection?: boolean;
      first?: boolean;
      last?: boolean;
      lastQuestion?: boolean;
    };
    const items: Item[] = [];
    folderIds.forEach((folderId) => {
      const sectionItems: Item[] = [];
      (folderHasSections[folderId] ?? []).forEach((sectionId) => {
        const questionItems: Item[] = [];
        (sectionHasQuestions[sectionId] ?? []).forEach((questionId) =>
          questionItems.push({
            type: 'question',
            id: questionId,
          }),
        );
        if (questionItems.length) {
          sectionItems.push({
            type: 'section',
            id: sectionId,
            firstSection: !sectionItems.length,
            first: true,
          });
          questionItems[questionItems.length - 1]!.last = true;
          sectionItems.push(...questionItems);
        }
      });
      if (sectionItems.length) {
        sectionItems[sectionItems.length - 1]!.lastQuestion = true;
        items.push({
          type: 'folder',
          id: folderId,
        });
        items.push(...sectionItems);
      }
    });
    return items;
  },
);

export const selectAseSetupLibraryFolderItemList = createCachedSelector(
  selectAseSetupLibraryCollapsedSections,
  selectAseSetupLibraryFoldersHaveSections,
  selectAseSetupLibrarySectionsHaveQuestions,
  selectAseSetupLibraryQuestionsHaveAnswers,
  selectAseSetupLibraryAnswersHaveQuestions,
  (_: RemoteState, folderId: number) => folderId,
  (
    collapsedSectionIds,
    folderHasSections,
    sectionHasQuestions,
    questionHasAnswers,
    answerHasQuestions,
    folderId,
  ) => {
    type Item = {
      type: 'section' | 'question' | 'addsection' | 'addquestion';
      id: number;
      level: number;
      last: boolean;
    };

    const items: Item[] = [];

    const cascadeQuestion = (
      questionId: number,
      questionLevel: number,
      last: boolean,
      parentLast: boolean,
    ): Item[] => {
      const moreItems: Item[] = [];
      const childAnswerIds = questionHasAnswers[questionId] ?? [];
      const hasChildQuestions = childAnswerIds.some(
        (answerId) => !!answerHasQuestions[answerId]?.length,
      );
      moreItems.push({
        type: 'question',
        id: questionId,
        level: questionLevel,
        last: parentLast && last && !hasChildQuestions,
      });
      (questionHasAnswers[questionId] ?? []).forEach((answerId) => {
        const questionIds = answerHasQuestions[answerId] ?? [];
        const lastQuestionId = questionIds[questionIds.length - 1];
        questionIds.forEach((subQuestionId) =>
          moreItems.push(
            ...cascadeQuestion(
              subQuestionId,
              questionLevel + 1,
              subQuestionId === lastQuestionId,
              parentLast && last,
            ),
          ),
        );
      });
      return moreItems;
    };

    (folderHasSections[folderId] ?? []).forEach((sectionId) => {
      items.push({
        type: 'section',
        id: sectionId,
        level: 0,
        last: false,
      });
      if (!collapsedSectionIds.includes(sectionId)) {
        const questionIds = sectionHasQuestions[sectionId] ?? [];
        const lastQuestionId = questionIds[questionIds.length - 1];
        questionIds.forEach((questionId) =>
          items.push(
            ...cascadeQuestion(
              questionId,
              0,
              questionId === lastQuestionId,
              true,
            ),
          ),
        );
        items.push({
          type: 'addquestion',
          id: sectionId,
          level: 0,
          last: false,
        });
      }
    });

    if (items.length) {
      items.push({
        type: 'addsection',
        id: folderId,
        level: 0,
        last: false,
      });
    }

    return items;
  },
)((_, folderId) => folderId);

export const selectAseSetupLibrarySectionIsCollapsed = createCachedSelector(
  selectRemote,
  (_: RemoteState, id: number) => id,
  (remote, id) => remote.aseLibraryCollapsedSections.includes(id),
)((_, id) => id);

export const selectAseSetupLibraryFolder = createCachedSelector(
  selectAseSetupLibraryFolders,
  (_: RemoteState, folderId: number) => folderId,
  (folders, folderId) => folders[folderId],
)((_, folderId) => folderId);

export const selectAseSetupLibrarySection = createCachedSelector(
  selectAseSetupLibrarySections,
  (_: RemoteState, sectionId: number) => sectionId,
  (sections, sectionId) => sections[sectionId],
)((_, sectionId) => sectionId);

export const selectAseSetupLibraryQuestion = createCachedSelector(
  selectAseSetupLibraryQuestions,
  (_: RemoteState, questionId: number) => questionId,
  (questions, questionId): AssessmentLibrary['questions'][number] | undefined =>
    questions[questionId],
)((_, questionId) => questionId);

const selectAseSetupLibraryQuestionsHaveChecklistItems = createSelector(
  selectAseSetupLibraryChecklistItems,
  (checklistItems) =>
    sortBy(Object.values(checklistItems), 'ordering').reduce<
      Record<string, number[]>
    >((acc, checklistItem) => {
      if (!acc[checklistItem.questionId]) {
        acc[checklistItem.questionId] = [];
      }
      acc[checklistItem.questionId]!.push(checklistItem.id);
      return acc;
    }, {}),
);

export const selectAseSetupLibraryAnswer = createCachedSelector(
  selectAseSetupLibraryAnswers,
  (_: RemoteState, answerId: number) => answerId,
  (answers, answerId) => answers[answerId],
)((_, answerId) => answerId);

export const selectAseSetupLibraryChecklistItem = createCachedSelector(
  selectAseSetupLibraryChecklistItems,
  (_: RemoteState, checklistItemId: number) => checklistItemId,
  (checklists, checklistItemId) => checklists[checklistItemId],
)((_, checklistItemId) => checklistItemId);

export const selectAseSetupLibraryActionDefinition = createCachedSelector(
  selectAseSetupLibraryActionDefinitions,
  (_: RemoteState, actionDefinitionId: string) => actionDefinitionId,
  (actionDefinitions, actionDefinitionId) =>
    actionDefinitions[actionDefinitionId],
)((_, actionDefinitionId) => actionDefinitionId);

export const selectAseSetupLibraryQuestionHasChecklistItems =
  createCachedSelector(
    selectAseSetupLibraryQuestionsHaveChecklistItems,
    (_: RemoteState, questionId: number) => questionId,
    (questionsHaveChecklistItems, questionId) =>
      questionsHaveChecklistItems[questionId] || [],
  )((_, questionId) => questionId);

const selectAseSetupLibraryAnswersHaveActions = createSelector(
  selectAseSetupActions,
  (actions): Record<string, number[]> =>
    Object.values(actions).reduce<Record<string, number[]>>((acc, action) => {
      action.answerIds.forEach((answerId) => {
        if (!acc[answerId]) {
          acc[answerId] = [];
        }
        acc[answerId]!.push(action.id);
      });
      return acc;
    }, {}),
);

export const selectAseSetupAnswerHasActions = createCachedSelector(
  selectAseSetupLibraryAnswersHaveActions,
  (_: RemoteState, answerId: number) => answerId,
  (answersHaveActions, answerId): number[] =>
    answersHaveActions[answerId] ?? [],
)((_, answerId) => answerId);

const selectAseSetupLibraryQuestionsHaveControls = createSelector(
  selectAseSetupControls,
  (controls): Record<string, number[]> =>
    Object.values(controls).reduce<Record<string, number[]>>((acc, action) => {
      action.questionIds.forEach((questionId) => {
        if (!acc[questionId]) {
          acc[questionId] = [];
        }
        acc[questionId]!.push(action.id);
      });
      return acc;
    }, {}),
);

export const selectAseSetupQuestionHasControls = createCachedSelector(
  selectAseSetupLibraryQuestionsHaveControls,
  (_: RemoteState, questionId: number) => questionId,
  (questionsHaveControls, questionId): number[] =>
    questionsHaveControls[questionId] ?? [],
)((_, questionId) => questionId);

const selectAseSetupLibraryChecklistItemsHaveActions = createSelector(
  selectAseSetupActions,
  (actions): Record<string, { negative: number[]; positive: number[] }> =>
    Object.values(actions).reduce<
      Record<string, { negative: number[]; positive: number[] }>
    >((acc, action) => {
      action.negativeChecklistItemIds.forEach((checklistItemId) => {
        if (!acc[checklistItemId]) {
          acc[checklistItemId] = {
            negative: [],
            positive: [],
          };
        }
        acc[checklistItemId]!.negative.push(action.id);
      });
      action.positiveChecklistItemIds.forEach((checklistItemId) => {
        if (!acc[checklistItemId]) {
          acc[checklistItemId] = {
            negative: [],
            positive: [],
          };
        }
        acc[checklistItemId]!.positive.push(action.id);
      });
      return acc;
    }, {}),
);

export const selectAseSetupChecklistItemHasActions = createCachedSelector(
  selectAseSetupLibraryChecklistItemsHaveActions,
  (_: RemoteState, questionId: number) => questionId,
  (
    checklistsHaveActions,
    questionId,
  ): { negative: number[]; positive: number[] } =>
    checklistsHaveActions[questionId] || {
      negative: [],
      positive: [],
    },
)((_, questionId) => questionId);

const selectAseSetupLibraryChecklistItemsHaveControls = createSelector(
  selectAseSetupControls,
  (controls): Record<string, { negative: number[]; positive: number[] }> =>
    Object.values(controls).reduce<
      Record<string, { negative: number[]; positive: number[] }>
    >((acc, control) => {
      control.negativeChecklistItemIds.forEach((checklistItemId) => {
        if (!acc[checklistItemId]) {
          acc[checklistItemId] = {
            negative: [],
            positive: [],
          };
        }
        acc[checklistItemId]!.negative.push(control.id);
      });
      control.positiveChecklistItemIds.forEach((checklistItemId) => {
        if (!acc[checklistItemId]) {
          acc[checklistItemId] = {
            negative: [],
            positive: [],
          };
        }
        acc[checklistItemId]!.positive.push(control.id);
      });
      return acc;
    }, {}),
);

export const selectAseSetupChecklistItemHasControls = createCachedSelector(
  selectAseSetupLibraryChecklistItemsHaveControls,
  (_: RemoteState, questionId: number) => questionId,
  (
    checklistsHaveControls,
    questionId,
  ): { negative: number[]; positive: number[] } =>
    checklistsHaveControls[questionId] || {
      negative: [],
      positive: [],
    },
)((_, questionId) => questionId);

export const selectRemoteAseSetupAction = createCachedSelector(
  selectAseSetupActions,
  (_: RemoteState, actionId: number) => actionId,
  (actions, actionId) => actions[actionId],
)((_, actionId) => actionId);

export const selectRemoteAseSetupControl = createCachedSelector(
  selectAseSetupControls,
  (_: RemoteState, controlId: number) => controlId,
  (controls, controlId) => controls[controlId],
)((_, controlId) => controlId);

export const selectRemoteSuggestedActionControl = createCachedSelector(
  selectAseSetupActions,
  selectAseSetupControls,
  (_: RemoteState, data: { type: 'action' | 'control'; id: number }) => data,
  (
    actions,
    controls,
    data,
  ): null | {
    description: string;
    why: string;
    severityId: number;
    timescaleId: number;
    categoryId: number;
    questionIds: number[];
    answerIds: number[];
    positiveChecklistItemIds: number[];
    negativeChecklistItemIds: number[];
  } => {
    const finding =
      data.type === 'action' ? actions[data.id] : controls[data.id];
    if (!finding) {
      return null;
    }
    return {
      description: finding.what,
      why: finding.why,
      severityId: objectHasOwnProperty(finding, 'severityId')
        ? (finding.severityId as number)
        : 0,
      timescaleId: objectHasOwnProperty(finding, 'timescaleId')
        ? (finding.timescaleId as number)
        : 0,
      categoryId: objectHasOwnProperty(finding, 'categoryId')
        ? (finding.categoryId as number)
        : 0,
      questionIds: objectHasOwnProperty(finding, 'questionIds')
        ? (finding.questionIds as number[])
        : [],
      answerIds: objectHasOwnProperty(finding, 'answerIds')
        ? (finding.answerIds as number[])
        : [],
      positiveChecklistItemIds: finding.positiveChecklistItemIds,
      negativeChecklistItemIds: finding.negativeChecklistItemIds,
    };
  },
)((_, { type, id }) => `${type}-${id}`);

export const selectAseSetupLibraryFolderSubItems = createCachedSelector(
  selectAseSetupLibraryFoldersHaveSections,
  selectAseSetupLibrarySectionsHaveQuestions,
  selectAseSetupLibraryQuestionsHaveAnswers,
  selectAseSetupLibraryQuestionsHaveChecklistItems,
  selectAseSetupLibraryAnswersHaveQuestions,
  selectAseSetupLibraryQuestions,
  (_: RemoteState, folderId: number) => folderId,
  (
    folderSections,
    sectionQuestions,
    questionAnswers,
    questionChecklistItems,
    answerQuestions,
    questions,
    folderId,
  ): {
    sectionIds: number[];
    answerIds: number[];
    questionIds: number[];
    checklistIds: number[];
    checklistItemIds: number[];
  } => {
    const sectionIds: number[] = [];
    const questionIds: number[] = [];
    const checklistIds: number[] = [];
    const checklistItemIds: number[] = [];
    const answerIds: number[] = [];

    const cascadeQuestions = (thsQuestionId: number) => {
      if (questions[thsQuestionId]?.type === AssessmentQuestionType.checklist) {
        checklistIds.push(thsQuestionId);
        (questionChecklistItems[thsQuestionId] ?? []).forEach((id) => {
          checklistItemIds.push(id);
        });
      } else {
        questionIds.push(thsQuestionId);
        (questionAnswers[thsQuestionId] || []).forEach((thsAnswerId) => {
          answerIds.push(thsAnswerId);
          (answerQuestions[thsAnswerId] || []).forEach((thsSubQuestionId) => {
            questionIds.push(thsSubQuestionId);
            cascadeQuestions(thsSubQuestionId);
          });
        });
      }
    };

    (folderSections[folderId] ?? []).forEach((sectionId) => {
      sectionIds.push(sectionId);
      (sectionQuestions[sectionId] ?? []).forEach((questionId) => {
        cascadeQuestions(questionId);
      });
    });

    return {
      sectionIds,
      answerIds,
      questionIds,
      checklistIds,
      checklistItemIds,
    };
  },
)((_, folderId) => folderId);

export const selectAseSetupLibrarySectionSubItems = createCachedSelector(
  selectAseSetupLibrarySectionsHaveQuestions,
  selectAseSetupLibraryQuestionsHaveChecklistItems,
  selectAseSetupLibraryQuestionsHaveAnswers,
  selectAseSetupLibraryAnswersHaveQuestions,
  selectAseSetupLibraryQuestions,
  (_: RemoteState, sectionId: number) => sectionId,
  (_, __, directChildrenOnly: boolean) => directChildrenOnly,
  (
    sectionQuestions,
    questionChecklistItems,
    questionAnswers,
    answerQuestions,
    questions,
    sectionId,
    directChildrenOnly,
  ): {
    answerIds: number[];
    questionIds: number[];
    checklistItemIds: number[];
    checklistIds: number[];
  } => {
    const questionIds: number[] = [];
    const checklistIds: number[] = [];
    const checklistItemIds: number[] = [];
    const answerIds: number[] = [];

    const cascadeQuestions = (thsQuestionId: number) => {
      if (questions[thsQuestionId]?.type === AssessmentQuestionType.checklist) {
        checklistIds.push(thsQuestionId);
        (questionChecklistItems[thsQuestionId] ?? []).forEach((id) => {
          checklistItemIds.push(id);
        });
      } else {
        questionIds.push(thsQuestionId);
        (questionAnswers[thsQuestionId] || []).forEach((thsAnswerId) => {
          answerIds.push(thsAnswerId);
          if (!directChildrenOnly) {
            (answerQuestions[thsAnswerId] || []).forEach((thsSubQuestionId) => {
              questionIds.push(thsSubQuestionId);
              cascadeQuestions(thsSubQuestionId);
            });
          }
        });
      }
    };

    (sectionQuestions[sectionId] ?? []).forEach((questionId) => {
      cascadeQuestions(questionId);
    });

    return {
      answerIds,
      questionIds,
      checklistIds,
      checklistItemIds,
    };
  },
)(
  (_, sectionId, directChildrenOnly) =>
    `${sectionId}-${directChildrenOnly ? 'd' : 'h'}`,
);

export const selectAseSetupLibraryQuestionSubItems = createCachedSelector(
  selectAseSetupLibraryQuestionsHaveAnswers,
  selectAseSetupLibraryAnswersHaveQuestions,
  selectAseSetupLibraryQuestionsHaveChecklistItems,
  selectAseSetupLibraryQuestionsHaveControls,
  selectAseSetupLibraryAnswersHaveActions,
  selectAseSetupLibraryChecklistItemsHaveActions,
  selectAseSetupLibraryChecklistItemsHaveControls,
  selectAseSetupLibraryQuestions,
  (_: RemoteState, questionId: number) => questionId,
  (_, __, directChildrenOnly: boolean) => directChildrenOnly,
  (
    questionAnswers,
    answerQuestions,
    questionChecklistItems,
    questionsControls,
    answersActions,
    checklistItemActions,
    checklistItemControls,
    questions,
    questionId,
    directChildrenOnly,
  ): {
    answerIds: number[];
    questionIds: number[];
    checklistIds: number[];
    checklistItemIds: number[];
    actionIds: number[];
    controlIds: number[];
  } => {
    const answerIds: number[] = [];
    const questionIds: number[] = [];
    const checklistIds: number[] = [];
    const checklistItemIds: number[] = [];
    const actionIds: number[] = [];
    const controlIds: number[] = [];

    const cascadeQuestions = (thsQuestionId: number) => {
      if (questions[thsQuestionId]?.type === AssessmentQuestionType.checklist) {
        if (thsQuestionId !== questionId) {
          checklistIds.push(thsQuestionId);
        }
        (questionChecklistItems[questionId] ?? []).forEach((id) => {
          checklistItemIds.push(id);
          actionIds.push(...(checklistItemActions[id]?.negative ?? []));
          actionIds.push(...(checklistItemActions[id]?.positive ?? []));
          controlIds.push(...(checklistItemControls[id]?.negative ?? []));
          controlIds.push(...(checklistItemControls[id]?.positive ?? []));
        });
      } else {
        if (thsQuestionId !== questionId) {
          questionIds.push(thsQuestionId);
        }
        controlIds.push(...(questionsControls[thsQuestionId] ?? []));
        (questionAnswers[thsQuestionId] || []).forEach((thsAnswerId) => {
          answerIds.push(thsAnswerId);
          actionIds.push(...(answersActions[thsAnswerId] ?? []));
          if (!directChildrenOnly) {
            (answerQuestions[thsAnswerId] || []).forEach((thsSubQuestionId) => {
              questionIds.push(thsSubQuestionId);
              cascadeQuestions(thsSubQuestionId);
            });
          }
        });
      }
    };

    cascadeQuestions(questionId);

    return {
      answerIds: arrayUnique(answerIds),
      questionIds: arrayUnique(questionIds),
      checklistIds: arrayUnique(checklistIds),
      checklistItemIds: arrayUnique(checklistItemIds),
      actionIds: arrayUnique(actionIds),
      controlIds: arrayUnique(controlIds),
    };
  },
)(
  (_, questionId, directChildrenOnly) =>
    `${questionId}-${directChildrenOnly ? 'd' : 'h'}`,
);

export const selectAseSetupLibraryAnswerSubItems = createCachedSelector(
  selectAseSetupLibraryQuestionsHaveAnswers,
  selectAseSetupLibraryQuestionsHaveChecklistItems,
  selectAseSetupLibraryAnswersHaveQuestions,
  selectAseSetupLibraryQuestionsHaveControls,
  selectAseSetupLibraryAnswersHaveActions,
  selectAseSetupLibraryChecklistItemsHaveActions,
  selectAseSetupLibraryChecklistItemsHaveControls,
  selectAseSetupLibraryQuestions,
  (_: RemoteState, answerId: number) => answerId,
  (
    questionAnswers,
    questionChecklistItems,
    answerQuestions,
    questionsControls,
    answersActions,
    checklistItemActions,
    checklistItemControls,
    questions,
    answerId,
  ): {
    answerIds: number[];
    questionIds: number[];
    checklistIds: number[];
    checklistItemIds: number[];
    actionIds: number[];
    controlIds: number[];
  } => {
    const answerIds: number[] = [];
    const questionIds: number[] = [];
    const checklistIds: number[] = [];
    const checklistItemIds: number[] = [];
    const actionIds: number[] = [];
    const controlIds: number[] = [];

    const cascadeAnswers = (thsAnswerId: number) => {
      actionIds.push(...(answersActions[thsAnswerId] ?? []));
      (answerQuestions[thsAnswerId] || []).forEach((thsSubQuestionId) => {
        if (
          questions[thsSubQuestionId]?.type === AssessmentQuestionType.checklist
        ) {
          checklistIds.push(thsSubQuestionId);
          (questionChecklistItems[thsSubQuestionId] ?? []).forEach((id) => {
            checklistItemIds.push(id);
            actionIds.push(...(checklistItemActions[id]?.negative ?? []));
            actionIds.push(...(checklistItemActions[id]?.positive ?? []));
            controlIds.push(...(checklistItemControls[id]?.negative ?? []));
            controlIds.push(...(checklistItemControls[id]?.positive ?? []));
          });
        } else {
          questionIds.push(thsSubQuestionId);
          controlIds.push(...(questionsControls[thsSubQuestionId] ?? []));
          (questionAnswers[thsSubQuestionId] || []).forEach(
            (thsSubAnswerId) => {
              answerIds.push(thsSubAnswerId);
              cascadeAnswers(thsSubAnswerId);
            },
          );
        }
      });
    };

    cascadeAnswers(answerId);

    return {
      answerIds: arrayUnique(answerIds),
      questionIds: arrayUnique(questionIds),
      checklistIds: arrayUnique(checklistIds),
      checklistItemIds: arrayUnique(checklistItemIds),
      actionIds: arrayUnique(actionIds),
      controlIds: arrayUnique(controlIds),
    };
  },
)((_, answerId) => answerId);

// Billing
export const selectBillingInvoices = createSelector(
  selectRemote,
  (state) => state.billingInvoices,
);

export const selectBillingStatement = createSelector(
  selectRemote,
  (state) => state.billingStatement,
);

export const selectBillingDraftInvoices = createSelector(
  selectRemote,
  (state) => state.billingDraftInvoices,
);

export const selectBillingOrganisation = createSelector(
  selectRemote,
  (state) => state.billingOrganisation,
);
export const selectBillingProducts = createSelector(
  selectRemote,
  (state) => state.billingProducts,
);
export const selectBillingProductIds = createSelector(
  selectBillingProducts,
  (products) => sortBy(products || [], 'name').map((p) => p.id),
);

export const selectBillingProduct = createCachedSelector(
  [selectBillingProducts, (_: State, productId: string) => productId],
  (products, productId) => (products || []).find((p) => p.id === productId),
)((_: State, productId: string) => productId);

export const selectBillingProductsUnits = createSelector(
  selectRemote,
  (state) => state.billingProductsUnits,
);
export const selectBillingProductUnits = createCachedSelector(
  [selectBillingProductsUnits, (_: State, productId: string) => productId],
  (productUnits, productId) =>
    (productUnits || []).find((pu) => pu.productId === productId),
)((_: State, productId: string) => productId);

const selectBillingDiscounts = createSelector(
  selectRemote,
  (state) => state.billingDiscounts,
);
export const selectBillingDiscount = createCachedSelector(
  [selectBillingDiscounts, (_: State, itemId: string) => itemId],
  (items, itemId) => (items || []).find((p) => p.id === itemId),
)((_: State, itemId: string) => itemId);

export const selectBillingDiscountIds = createSelector(
  selectBillingDiscounts,
  (items) => sortBy(items || []).map((i) => i.id),
);

const selectBillingItems = createSelector(
  selectRemote,
  (state) => state.billingItems,
);
export const selectBillingItem = createCachedSelector(
  [selectBillingItems, (_: State, itemId: string) => itemId],
  (items, itemId) => (items || []).find((p) => p.id === itemId),
)((_: State, itemId: string) => itemId);

export const selectBillingItemIds = createSelector(
  selectBillingItems,
  (items) => sortBy(items || []).map((i) => i.id),
);

// Reports

const selectRpt = createSelector(selectRemote, (state) => state.reporting);

export const selectRptLoadStatus = createSelector(
  selectRemote,
  (state) => state.reporting.loadStatus,
);

export const selectRptReportPages = createSelector(
  selectRemote,
  ({ pages }) => pages,
);

export const selectRptReportPagesSaved = createSelector(
  selectRemote,
  ({ pagesSaved }) => pagesSaved,
);

const selectRptReport = createSelector(
  [selectRpt, (_: State, key: string) => key],
  (reporting, key) => reporting.reports[key],
);

export const selectRptReportBasics = createSelector(
  selectRptReport,
  (
    report,
  ):
    | undefined
    | {
        id: string;
        name: string;
        description: string;
        menuDescription: string;
        icon: ICONS;
        type: ReportEnum;
        typeName: string;
        typeIsDefault: boolean;
      } => {
    return !report
      ? undefined
      : {
          id: report.id,
          name: report.name,
          description: report.description,
          menuDescription: report.menuDescription,
          icon: report.icon,
          type: report.type,
          typeName: report.typeName,
          typeIsDefault: report.typeIsDefault,
        };
  },
);

export const selectRptReportName = createSelector(
  selectRptReportBasics,
  (basics) => basics?.name,
);

export const selectRptReportPageNumber = createSelector(
  selectRptReport,
  (report) => report?.pageNumber || 1,
);

export const selectRptReportIsLoading = createSelector(
  selectRptReport,
  (report) => !!report?.isLoading,
);

export const selectRptReportType = createSelector(
  selectRptReport,
  (report) => report?.type,
);

export const selectRptReportLoaded = createSelector(
  selectRptReport,
  (report) => !!report?.loaded,
);

export const selectRptReportVisuals = createSelector(
  selectRptReport,
  (report) => report?.visuals || [],
);

export const selectRptReportHasVisuals = createSelector(
  selectRptReport,
  (report) => !!report?.visuals?.length,
);

export const selectRptReportVisualsSize = createSelector(
  (state: State) => selectGeneric(state, genericReportingVisualsSize),
  (value) =>
    validateEnumFallback(
      ReportingVisualsSizeEnum,
      value,
      ReportingVisualsSizeEnum.small,
    ),
);

export const selectRptReportTotalCount = createSelector(
  selectRptReport,
  (report) => report?.totalCount || 0,
);

// export const selectRptReportTotalCount = createSelector(
//   selectRptReport,
//   (report) => report?.totalCount || 0,
// );

export const selectRptReportFilterOptions = createSelector(
  selectRptReport,
  (report) => report?.filterOptions,
);

export const selectRptReportFilterValues = createSelector(
  selectRptReport,
  (report) => report?.filterValues,
);

export const selectRptReportDraftFilterValues = createSelector(
  selectRptReport,
  (report) => report?.draftFilterValues,
);

export const selectRptReportDraftFilterIsDirty = createSelector(
  selectRptReport,
  (report) =>
    !isEqual(report?.filterValues || {}, report?.draftFilterValues || {}),
);

export const selectRptReportOrder = createSelector(
  selectRptReport,
  (report) => report?.order || [],
);

export const selectRptReportItem = createSelectorCached(
  [selectRptReport, (_: State, __: string, itemKey: string) => itemKey],
  (report, itemKey) => report?.data[itemKey],
);

/**
 * API Settings
 */

export const selectApiSettingsUsers = createSelector(
  selectRemote,
  (remote) => remote.apiUsers,
);

export const selectApiSettingsWebhooks = createSelector(
  selectRemote,
  (remote) => remote.apiWebhooks,
);

/**
 * Engage
 */

export const selectEngageSettings = createSelector(
  selectRemote,
  (remote) => remote.engage,
);

export const selectEngageDocuments = createSelector(
  selectEngageSettings,
  (engage): EngageDocument[] => engage!.documents,
);

export const selectEngageElements = createSelector(
  selectEngageSettings,
  (engage): EngageElement[] => engage!.elements,
);

/**
 * SSO
 */

export const selectSsoSettings = createSelector(
  selectRemote,
  (remote) => remote.ssoSettings,
);

/**
 * Organisation
 */

export const selectSetupOrganisation = createSelector(
  selectRemote,
  (remote) => remote.setupOrganisation,
);
